import {
  autoUpdate,
  flip,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';
import { match } from 'ts-pattern';
import { uniq } from '../../lib/arrayHelpers';
import type {
  BaseOption,
  FlatOptions,
  GroupedOptions,
  MultiSelectOptions,
  SelectProps,
} from './types';

// Check if options are grouped
export const isGroupedOptions = <T, B extends BaseOption<T> = BaseOption<T>>(
  options: MultiSelectOptions<T, B>
): options is GroupedOptions<T, B> => {
  return (
    Array.isArray(options) &&
    options.length > 0 &&
    options.every(
      (item) =>
        typeof item === 'object' &&
        item !== null &&
        'options' in item &&
        Array.isArray(item.options)
    )
  );
};

// Convert options, both grouped and flat, to flat
const flattenOptions = <T>(options: MultiSelectOptions<T>): FlatOptions<T> =>
  isGroupedOptions(options) ? options.flatMap((group) => group.options) : options;

// Convert options, both grouped and flat, to grouped
const normalizeToGroups = <T>(options: MultiSelectOptions<T>): GroupedOptions<T> =>
  isGroupedOptions(options) ? options : [{ options: options }];

// Apply search filter to grouped options
const filterGroups = <T>(groups: GroupedOptions<T>, searchQuery: string): GroupedOptions<T> => {
  if (!searchQuery) return groups;

  const query = searchQuery.toLowerCase();
  const filtered: GroupedOptions<T> = [];

  groups.forEach((group) => {
    const filteredOptions = group.options.filter((option) =>
      (option.label ?? String(option.value)).toLowerCase().includes(query)
    );
    if (filteredOptions.length > 0) {
      filtered.push({
        ...group,
        options: filteredOptions,
      });
    }
  });

  return filtered;
};

export const useMultiSelectState = <T>(props: SelectProps<T>) => {
  const {
    options,
    multiselect = false,
    triggerLabel = 'Select',
    searchPlaceholder = 'Search...',
    panelOffset = 12,
    maxHeight = 200,
    onKeyboardShortcut,
  } = props;

  // Normalize options to flat format for most logic
  const flattenedOptions = useMemo(() => flattenOptions(options), [options]);

  const showSearchInput = props.showSearchInput ?? flattenedOptions.length > 5;
  const clearEnabled = props.clearEnabled ?? (multiselect && flattenedOptions.length > 5);

  const selectedValues: T[] = useMemo(() => {
    if (props.multiselect) {
      return props.values || [];
    } else {
      return props.value !== undefined ? [props.value] : [];
    }
  }, [props]);

  // State
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [isShiftKeyDown, setIsShiftKeyDown] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [triggerWidth, setTriggerWidth] = useState<number | null>(null);
  const [shouldFocusOnClose, setShouldFocusOnClose] = useState(false);

  // Refs
  const listRef = useRef<Array<HTMLElement | null>>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const clearButtonRef = useRef<HTMLButtonElement>(null);
  const triggerTextRef = useRef<HTMLSpanElement>(null);

  // Setup floating UI
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(panelOffset), flip()],
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
    placement: 'bottom-start',
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'listbox' });

  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    loop: true,
    focusItemOnOpen: false,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    click,
    dismiss,
    role,
    listNavigation,
  ]);

  // Flatten and filter groups based on search query
  const filteredGroups = useMemo(
    () => filterGroups(normalizeToGroups(options), searchQuery),
    [options, searchQuery]
  );
  // Flatten filtered options
  const filteredOptions = useMemo(
    () => filteredGroups.flatMap((group) => group.options),
    [filteredGroups]
  );

  // Pre-calculate flat indices for each option
  const optionIndices = useMemo(() => {
    const indices = new Map<BaseOption<T>, number>();
    let flatIndex = 0;
    filteredGroups.forEach((group) => {
      group.options.forEach((option) => {
        indices.set(option, flatIndex++);
      });
    });
    return indices;
  }, [filteredGroups]);

  // Get selected options
  const selectedOptions = flattenedOptions.filter((opt) =>
    selectedValues.some((selectedValue) => isEqual(selectedValue, opt.value))
  );

  // Generate trigger content
  const getSingleSelectionText = () => {
    const { label, value } = selectedOptions[0] ?? {};
    if (label) return label;
    if (typeof value === 'string' || typeof value === 'number') return value.toString();
    return '1 selected';
  };

  const triggerContent: string =
    triggerLabel === ''
      ? ''
      : match(selectedOptions.length)
          .with(0, () => triggerLabel.toString())
          .with(1, getSingleSelectionText)
          .otherwise(() => `${selectedValues.length} selected`);

  // Generate IDs
  const triggerLabelId = useId();

  // Toggle option handler
  const handleToggleOption = useCallback(
    (selectedValue: T, index: number, isShiftKey: boolean = false) => {
      const selectedOption = filteredOptions.find((option) => isEqual(option.value, selectedValue));

      if (selectedOption?.disabled) {
        return;
      }

      if (props.multiselect) {
        const onChangeMulti = props.onChange;

        if (isShiftKey && lastSelectedIndex !== null) {
          const startIndex = Math.min(lastSelectedIndex, index);
          const endIndex = Math.max(lastSelectedIndex, index);

          const valuesInRange = filteredOptions
            .slice(startIndex, endIndex + 1)
            .filter((option) => !option.disabled)
            .map((option) => option.value);

          const isEndIndexSelected = selectedValues.includes(selectedValue);

          onChangeMulti(
            isEndIndexSelected
              ? selectedValues.filter((v) => !valuesInRange.includes(v))
              : uniq([...selectedValues, ...valuesInRange])
          );
        } else {
          if (selectedValues.includes(selectedValue)) {
            onChangeMulti(selectedValues.filter((v) => v !== selectedValue));
          } else {
            onChangeMulti([...selectedValues, selectedValue]);
          }
        }

        setLastSelectedIndex(index);
      } else {
        props.onChange(selectedValue);
        setIsOpen(false);
      }
    },
    [props, lastSelectedIndex, filteredOptions, selectedValues]
  );

  // Clear selection handler
  const handleClearSelection = useCallback(() => {
    if (props.multiselect) {
      props.onChange([]);
    } else {
      props.onChange(undefined);
    }
    setSearchQuery('');
    setLastSelectedIndex(null);
    searchInputRef.current?.focus();
  }, [props]);

  // Track shift key state
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setIsShiftKeyDown(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setIsShiftKeyDown(false);
      }
    };

    const handleBlur = () => setIsShiftKeyDown(false);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Search input key down handler
  const handleSearchInputKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Tab' && !e.shiftKey) {
        const firstSelectableIndex = filteredOptions.findIndex((opt) => !opt.disabled);
        if (firstSelectableIndex >= 0) {
          setActiveIndex(firstSelectableIndex);
          if (listRef.current[firstSelectableIndex]) {
            e.preventDefault();
            listRef.current[firstSelectableIndex].focus();
          }
        }
      } else if (e.key === 'Enter') {
        if (activeIndex === null || activeIndex < filteredOptions.length) {
          let indexToUse = activeIndex ?? filteredOptions.findIndex((opt) => !opt.disabled);
          if (indexToUse === -1) {
            setIsOpen(false);
            e.preventDefault();
            e.stopPropagation();
            return;
          }

          const selectedOption = filteredOptions[indexToUse];
          if (selectedOption && !selectedOption.disabled) {
            handleToggleOption(selectedOption.value, indexToUse);
            if (!multiselect) {
              setIsOpen(false);
            }
          }
        } else {
          setIsOpen(false);
        }
        e.preventDefault();
        e.stopPropagation();
      } else if (e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();

        const input = e.target as HTMLInputElement;
        const cursorPosition = input.selectionStart ?? 0;
        const currentValue = input.value;
        const newValue =
          currentValue.substring(0, cursorPosition) +
          ' ' +
          currentValue.substring(input.selectionEnd ?? cursorPosition);
        setSearchQuery(newValue);
        setTimeout(() => {
          input.setSelectionRange(cursorPosition + 1, cursorPosition + 1);
        }, 0);
      }
    },
    [activeIndex, filteredOptions, handleToggleOption, multiselect]
  );

  // Floating key down handler
  const handleFloatingKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Handle custom keyboard shortcuts first
      if (onKeyboardShortcut && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        const focusedOption =
          activeIndex !== null && filteredOptions[activeIndex]
            ? filteredOptions[activeIndex]
            : null;
        onKeyboardShortcut(e.key.toLowerCase(), focusedOption);
        // Don't prevent default here to allow the shortcut handler to decide
      }

      if (
        e.key.length === 1 &&
        e.key !== ' ' &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.metaKey &&
        searchInputRef.current &&
        e.target !== searchInputRef.current
      ) {
        searchInputRef.current.focus();
      }

      if (e.key === 'Enter' || (e.key === ' ' && e.target !== searchInputRef.current)) {
        if (document.activeElement === clearButtonRef.current) {
          handleClearSelection();
          e.preventDefault();
          return;
        }
        if (activeIndex !== null && filteredOptions[activeIndex]) {
          handleToggleOption(filteredOptions[activeIndex].value, activeIndex);

          if (!multiselect) {
            setIsOpen(false);
          }
          e.preventDefault();
        } else if (activeIndex === null || !multiselect || !showSearchInput) {
          setIsOpen(false);
          e.preventDefault();
        }
      }
    },
    [
      onKeyboardShortcut,
      showSearchInput,
      handleClearSelection,
      activeIndex,
      filteredOptions,
      multiselect,
      handleToggleOption,
    ]
  );

  // Reset search when closing dropdown
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setActiveIndex(null);
    }
  }, [isOpen]);

  // Focus the first result when search query changes
  useEffect(() => {
    const firstEnabledFilter = filteredOptions.findIndex((opt) => !opt.disabled);
    if (searchQuery && firstEnabledFilter > -1) {
      setActiveIndex(firstEnabledFilter);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 25);
    }
  }, [searchQuery, filteredOptions.length, showSearchInput, filteredOptions]);

  // For single-select, start with the focus on the currently selected option
  useEffect(() => {
    if (!props.multiselect && props.value !== undefined) {
      setTimeout(() => {
        const selectedOptionIndex = filteredOptions.findIndex(
          (option) => isEqual(option.value, props.value) && !option.disabled
        );
        if (selectedOptionIndex >= 0) {
          setActiveIndex(selectedOptionIndex);
        } else {
          const firstSelectableIndex = filteredOptions.findIndex((opt) => !opt.disabled);
          if (firstSelectableIndex >= 0) {
            setActiveIndex(firstSelectableIndex);
          }
        }
      }, 0);
    }
  }, [filteredOptions, props, isOpen]);

  // Handle focus on close
  useEffect(() => {
    if (!isOpen && shouldFocusOnClose) {
      // Focus the trigger element if it's an HTMLElement
      const referenceEl = refs.reference.current;
      if (referenceEl && 'focus' in referenceEl && typeof referenceEl.focus === 'function') {
        referenceEl.focus();
      }
      setShouldFocusOnClose(false);
    } else if (isOpen && !shouldFocusOnClose) {
      setShouldFocusOnClose(true);
    }
  }, [isOpen, refs, shouldFocusOnClose]);

  // Track trigger width
  useEffect(() => {
    if (triggerTextRef.current) {
      const width = triggerTextRef.current.offsetWidth;
      setTriggerWidth(width);
    }
  }, [triggerContent]);

  return {
    // Props
    ...props,
    // Modified props
    showSearchInput,
    clearEnabled,
    multiselect,
    triggerLabel,
    searchPlaceholder,
    panelOffset,
    maxHeight,

    // State
    isOpen,
    searchQuery,
    setSearchQuery,
    activeIndex,
    lastSelectedIndex,
    isShiftKeyDown,
    hoveredIndex,
    setHoveredIndex,
    triggerWidth,

    // Derived state
    selectedValues,
    selectedOptions,
    triggerContent,
    filteredGroups,
    filteredOptions,
    optionIndices,
    triggerLabelId,

    // Refs
    listRef,
    searchInputRef,
    clearButtonRef,
    triggerTextRef,

    // Floating UI
    refs,
    floatingStyles,
    context,
    getReferenceProps,
    getFloatingProps,
    getItemProps,

    // Handlers
    handleToggleOption,
    handleClearSelection,
    handleSearchInputKeyDown,
    handleFloatingKeyDown,
  };
};
